import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { FinancePageLayout, MoneyDisplay } from '@/components/finance';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useBankAccounts } from '@/hooks/finance';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { RefreshCw } from 'lucide-react';

type ImportSource = 'itau' | 'chase';
type PeriodFilter = '7' | '30' | '90' | 'all';

type ImportedTxnRow = {
  id: string;
  source: 'itau' | 'chase' | 'manual';
  booked_at: string;
  amount: number;
  currency: 'BRL' | 'USD';
  direction: 'in' | 'out';
  description: string | null;
  reference: string | null;
  batch_id: string;
};

function isSyntheticReference(reference: string | null | undefined): boolean {
  const value = String(reference || '').trim();
  if (!value) return false;
  // Fallback generated by parser: `${dateRaw}-${signedAmount}-${idx}`
  // Examples:
  // - 03/03/2026--9500-0
  // - 03/02/2026-5000-2
  return /^(\d{1,2}[/-]\d{1,2}[/-]\d{4})-[-]?\d+(\.\d+)?-\d+$/.test(value);
}

function formatBankBookedDate(value: string): string {
  if (!value) return '-';
  const isoPrefix = value.slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(isoPrefix)) {
    const [yyyy, mm, dd] = isoPrefix.split('-');
    return `${dd}/${mm}/${yyyy}`;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('pt-BR', { timeZone: 'UTC' });
}

function inferImportSourceFromName(name: string): ImportSource {
  const normalized = name.toLowerCase();
  if (normalized.includes('itaú') || normalized.includes('itau')) return 'itau';
  return 'chase';
}

function daysToStartDate(period: PeriodFilter): string | null {
  if (period === 'all') return null;
  const days = Number(period);
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString();
}

const PAGE_SIZE = 50;

export default function BankAccountTransactions() {
  const { accountId } = useParams();
  const [searchParams] = useSearchParams();
  const { data: accounts } = useBankAccounts('all');
  const account = useMemo(() => (accounts || []).find((row) => row.id === accountId), [accounts, accountId]);
  const batchIdFilter = searchParams.get('batch_id');

  const [period, setPeriod] = useState<PeriodFilter>('30');
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isReconciling, setIsReconciling] = useState(false);
  const [total, setTotal] = useState(0);
  const [transactions, setTransactions] = useState<ImportedTxnRow[]>([]);

  const source = useMemo(() => {
    if (!account) return 'itau' as ImportSource;
    return inferImportSourceFromName(`${account.bank_name || ''} ${account.name || ''}`);
  }, [account]);

  const loadTransactions = useCallback(async () => {
    if (!account) return;
    setIsLoading(true);
    try {
      const startDate = daysToStartDate(period);
      let countQuery = (supabase.from('bank_transactions_normalized' as any) as any)
        .select('id', { count: 'exact', head: true })
        .eq('source', source)
        .eq('currency', account.currency);
      let dataQuery = (supabase.from('bank_transactions_normalized' as any) as any)
        .select('id,source,booked_at,amount,currency,direction,description,reference,batch_id')
        .eq('source', source)
        .eq('currency', account.currency)
        .order('booked_at', { ascending: false });
      if (batchIdFilter) {
        countQuery = countQuery.eq('batch_id', batchIdFilter);
        dataQuery = dataQuery.eq('batch_id', batchIdFilter);
      }

      if (startDate) {
        countQuery = countQuery.gte('booked_at', startDate);
        dataQuery = dataQuery.gte('booked_at', startDate);
      }

      const from = (page - 1) * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;
      dataQuery = dataQuery.range(from, to);

      const [countRes, dataRes] = await Promise.all([countQuery, dataQuery]);
      if (countRes.error) throw countRes.error;
      if (dataRes.error) throw dataRes.error;
      setTotal(countRes.count || 0);
      setTransactions((dataRes.data || []) as ImportedTxnRow[]);
    } catch (error: any) {
      console.error('load bank account transactions error:', error);
      toast.error(`Falha ao carregar transações: ${error.message || 'erro desconhecido'}`);
    } finally {
      setIsLoading(false);
    }
  }, [account, page, period, source, batchIdFilter]);

  const runReconciliation = async () => {
    setIsReconciling(true);
    try {
      const { data, error } = await supabase.functions.invoke('reconcile-bank-transactions');
      if (error) throw error;
      toast.success(
        `Reconciliação: ${data?.matched_count || 0} match(es), ${data?.issues_created || 0} issue(s), ${data?.orphan_issues_created || 0} órfã(s).`,
      );
    } catch (error: any) {
      console.error('run reconciliation error:', error);
      toast.error(`Falha na reconciliação: ${error.message || 'erro desconhecido'}`);
    } finally {
      setIsReconciling(false);
    }
  };

  useEffect(() => {
    loadTransactions();
  }, [loadTransactions]);

  useEffect(() => {
    setPage(1);
  }, [period]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const summary = useMemo(() => {
    let inTotal = 0;
    let outTotal = 0;
    for (const tx of transactions) {
      const amount = Number(tx.amount || 0);
      if (tx.direction === 'in') inTotal += amount;
      else outTotal += amount;
    }
    return { inTotal, outTotal, net: inTotal - outTotal };
  }, [transactions]);

  return (
    <FinancePageLayout>
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Transações da Conta</h1>
          <p className="text-sm text-muted-foreground">
            {account ? `${account.name} • ${account.bank_name || 'Banco'}` : 'Conta não encontrada'}
          </p>
          {batchIdFilter ? (
            <p className="text-xs text-muted-foreground mt-1">
              Filtro ativo por lote: {batchIdFilter}
            </p>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" asChild>
            <Link to="/finance/bank-accounts">Voltar para Contas</Link>
          </Button>
          <Button variant="outline" asChild>
            <Link to="/finance/reconciliation?status=open">Fila de Conciliação</Link>
          </Button>
          {batchIdFilter ? (
            <Button variant="outline" asChild>
              <Link to={`/finance/bank-accounts/${accountId}/transactions`}>Limpar filtro de lote</Link>
            </Button>
          ) : null}
          <Button onClick={runReconciliation} disabled={isReconciling}>
            {isReconciling ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Reconciliar
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Filtros</CardTitle>
          <div className="w-[160px]">
            <Select value={period} onValueChange={(v: PeriodFilter) => setPeriod(v)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Últimos 7 dias</SelectItem>
                <SelectItem value="30">Últimos 30 dias</SelectItem>
                <SelectItem value="90">Últimos 90 dias</SelectItem>
                <SelectItem value="all">Todo período</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="border rounded-md p-2">
            <p className="text-xs text-muted-foreground">Saldo atual cadastrado</p>
            <p className="font-semibold">
              <MoneyDisplay value={Number(account?.current_balance || 0)} currency={(account?.currency || 'USD') as 'USD' | 'BRL'} />
            </p>
          </div>
          <div className="border rounded-md p-2">
            <p className="text-xs text-muted-foreground">Entradas (página atual)</p>
            <p className="font-semibold text-emerald-600">
              <MoneyDisplay value={summary.inTotal} currency={(account?.currency || 'USD') as 'USD' | 'BRL'} />
            </p>
          </div>
          <div className="border rounded-md p-2">
            <p className="text-xs text-muted-foreground">Saídas (página atual)</p>
            <p className="font-semibold text-red-600">
              <MoneyDisplay value={summary.outTotal} currency={(account?.currency || 'USD') as 'USD' | 'BRL'} />
            </p>
          </div>
          <div className="border rounded-md p-2">
            <p className="text-xs text-muted-foreground">Líquido (página atual)</p>
            <p className={`font-semibold ${summary.net >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
              <MoneyDisplay value={summary.net} currency={(account?.currency || 'USD') as 'USD' | 'BRL'} />
            </p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Transações Importadas</CardTitle>
          <Badge variant="outline">{total} registro(s)</Badge>
        </CardHeader>
        <CardContent className="space-y-2">
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Carregando transações...</p>
          ) : transactions.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nenhuma transação encontrada para os filtros atuais.</p>
          ) : (
            transactions.map((tx) => (
              <div key={tx.id} className="border rounded-md p-2 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{tx.description || tx.reference || 'Sem descrição'}</p>
                  <p className="text-xs text-muted-foreground">
                    {formatBankBookedDate(tx.booked_at)}
                    {!isSyntheticReference(tx.reference) && tx.reference ? ` • Ref: ${tx.reference}` : ''}
                  </p>
                </div>
                <p className={`text-sm font-semibold whitespace-nowrap ${tx.direction === 'in' ? 'text-emerald-600' : 'text-red-600'}`}>
                  {tx.direction === 'in' ? '+' : '-'} <MoneyDisplay value={Number(tx.amount || 0)} currency={tx.currency} />
                </p>
              </div>
            ))
          )}

          <div className="flex items-center justify-between pt-2">
            <p className="text-xs text-muted-foreground">
              Página {page} de {totalPages}
            </p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                Anterior
              </Button>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
                Próxima
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </FinancePageLayout>
  );
}
